import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Global keyboard shortcuts (§22.1) — Gmail/Linear/GitHub-style.
 *
 * Mounted once inside the authenticated AppShell. Purely additive: it adds
 * a single guarded `keydown` listener and a help overlay. It NEVER fires
 * while the user is typing (input / textarea / select / contenteditable) or
 * while a modifier key is held (so Cmd-R, Ctrl-C, etc. are untouched), and
 * navigation targets are permission-gated so a shortcut can't bounce a user
 * to /access-denied.
 *
 * Two-key `g` sequences (g→h, g→t, g→r) follow the established industry
 * pattern: a lone `g` does nothing, which is why single accidental
 * keystrokes can't trigger navigation. Press `?` for the cheat-sheet.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { hasPerm, useAuthStore } from '@/store/auth';
function isEditableTarget(el) {
    const node = el;
    if (!node || !node.tagName)
        return false;
    const tag = node.tagName;
    return (tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        node.isContentEditable === true);
}
export function GlobalShortcuts() {
    const navigate = useNavigate();
    const me = useAuthStore((s) => s.me);
    const [helpOpen, setHelpOpen] = useState(false);
    const canTickets = hasPerm(me, 'tickets.view');
    const canCreate = hasPerm(me, 'tickets.create');
    const canReports = hasPerm(me, 'reports.view');
    useEffect(() => {
        let pendingG = false;
        let gTimer;
        const clearG = () => {
            pendingG = false;
            if (gTimer)
                window.clearTimeout(gTimer);
        };
        const onKey = (e) => {
            // Never hijack browser/OS chords or in-field typing.
            if (e.metaKey || e.ctrlKey || e.altKey)
                return;
            if (isEditableTarget(e.target))
                return;
            // `?` toggles the cheat-sheet (works everywhere, harmless).
            if (e.key === '?') {
                e.preventDefault();
                setHelpOpen((v) => !v);
                return;
            }
            // Second key of a `g …` sequence.
            if (pendingG) {
                clearG();
                if (e.key === 'h') {
                    e.preventDefault();
                    navigate('/');
                }
                else if (e.key === 't' && canTickets) {
                    e.preventDefault();
                    navigate('/tickets');
                }
                else if (e.key === 'r' && canReports) {
                    e.preventDefault();
                    navigate('/reports');
                }
                return;
            }
            if (e.key === 'g') {
                // Start a short window to catch the second key.
                pendingG = true;
                if (gTimer)
                    window.clearTimeout(gTimer);
                gTimer = window.setTimeout(() => {
                    pendingG = false;
                }, 1200);
                return;
            }
            if (e.key === 'c' && canCreate) {
                e.preventDefault();
                navigate('/tickets/new');
            }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            clearG();
        };
    }, [navigate, canTickets, canCreate, canReports]);
    const shortcuts = [
        { combo: '?', label: 'Show / hide this help', show: true },
        { combo: 'g then h', label: 'Go to Home', show: true },
        { combo: 'g then t', label: 'Go to Tickets', show: canTickets },
        { combo: 'g then r', label: 'Go to Reports', show: canReports },
        { combo: 'c', label: 'Create a ticket', show: canCreate },
    ].filter((s) => s.show);
    return (_jsx(Modal, { open: helpOpen, onClose: () => setHelpOpen(false), title: "Keyboard shortcuts", size: "sm", children: _jsxs("div", { className: "space-y-2", children: [shortcuts.map((s) => (_jsxs("div", { className: "flex items-center justify-between gap-4 text-sm", children: [_jsx("span", { className: "text-slate-600 dark:text-slate-300", children: s.label }), _jsx("kbd", { className: "rounded border border-slate-300 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200", children: s.combo })] }, s.combo))), _jsx("p", { className: "pt-2 text-xs text-slate-400", children: "Shortcuts are ignored while you're typing in a field." })] }) }));
}
